import { Box, Typography } from '@mui/material';
import moment from 'moment';

import { FHTooltip } from '../../../../components/base';
import {
  FORTA_BLUE,
  FORTA_GRAY3,
  FORTA_ORANGE,
  FORTA_SKY_BLUE,
  FORTA_SUCCESS,
  FORTA_WHITE,
} from '../../../../utils/colors';
import { formatEntityId } from '../../../../helpers';
import { ENTITY_PREFIX } from '../../../../constants';
import { ACTIVATION_DATE_FORMAT } from './constants';
import { getDateDiffPercent } from './utils';
import ActiveIcon from '../../../../components/svgs/ActiveIcon';
import { TreatmentPlanStatus } from '../../../../components/layout/treatment-plan/constants/general';

import type { ActivationPeriod, TPActivationItem } from './TPActivationChart';

type TPRowProps = {
  tp: TPActivationItem;
  startDate: string;
  endDate: string;
  onBoxClick: (tpId: number, activationLog: ActivationPeriod) => void;
  timelineBackgroundColor?: string;
};

function TPRow({ tp, startDate, endDate, onBoxClick, timelineBackgroundColor }: TPRowProps) {
  const { tpId, isActive, status, activationsPeriods } = tp;
  const backgroundColor = timelineBackgroundColor || FORTA_SKY_BLUE;
  return (
    <Box
      sx={{
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        mb: 3,
      }}
    >
      <Box
        sx={{
          marginRight: 0.5,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          borderRadius: 1,
          px: 0.1,
        }}
      >
        <Box mb={isActive ? -0.6 : 0} mr={isActive ? -0.5 : 0}>
          {isActive ? (
            <ActiveIcon
              color={status === TreatmentPlanStatus.Completed ? FORTA_SUCCESS : FORTA_ORANGE}
            />
          ) : (
            <Box sx={{ width: 12, height: 12 }}></Box>
          )}
        </Box>
        <Typography
          sx={{
            px: 1,
            borderRadius: 1,
            fontSize: 12,
            fontWeight: 700,
            color: FORTA_BLUE.primary,
          }}
        >
          {formatEntityId(tpId, ENTITY_PREFIX.TreatmentPlan)}
        </Typography>
      </Box>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          height: 55,
          flex: 1,
          background: FORTA_WHITE,
          border: `1px solid ${FORTA_GRAY3}`,
        }}
      >
        {activationsPeriods.map((activationsPeriod: ActivationPeriod, idx: number) => {
          const { left, width } = getDateDiffPercent(
            startDate,
            endDate,
            activationsPeriod.activationStart,
            activationsPeriod.activationEnd,
          );
          const multipleActivations = activationsPeriods.length > 1;
          const isFirstActivation = idx === 0;
          const isLastActivation = idx === activationsPeriods.length - 1;
          return (
            <span key={idx}>
              <FHTooltip
                arrow
                tooltipBorder={`1px solid ${tp.isActive ? FORTA_SUCCESS : FORTA_SKY_BLUE}`}
                title={
                  <Box>
                    <Typography fontWeight={600} color={FORTA_BLUE.primary}>
                      Activation period
                    </Typography>
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 500, color: FORTA_BLUE.primary, mb: -1 }}
                    >
                      Start:{' '}
                      {moment(activationsPeriod.activationStart).format(ACTIVATION_DATE_FORMAT)}
                    </Typography>
                    <Typography
                      fontSize={14}
                      fontWeight={500}
                      color={FORTA_BLUE.primary}
                      display="inline"
                    >
                      End:
                    </Typography>
                    <Typography
                      sx={{
                        ml: 1,
                        fontSize: 14,
                        fontWeight: 500,
                        color: activationsPeriod.activationEnd ? FORTA_BLUE.primary : FORTA_SUCCESS,
                      }}
                      display="inline"
                    >
                      {activationsPeriod.activationEnd
                        ? moment(activationsPeriod.activationEnd).format(ACTIVATION_DATE_FORMAT)
                        : 'Currently Active'}
                    </Typography>
                  </Box>
                }
                placement="top-start"
                sx={{
                  borderRadius: '10px',
                  display: 'flex',
                  width: '240px',
                }}
                tooltipbackground={FORTA_WHITE}
                tooltipBoxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
              >
                <Box
                  key={idx}
                  sx={{
                    position: 'absolute',
                    left: `${left}%`,
                    width: `${width}%`,
                    height: '100%',
                    background: backgroundColor,
                    opacity: 0.9,
                    borderRight: `2px dotted ${FORTA_GRAY3}`,
                    borderLeft: `2px dotted ${FORTA_GRAY3}`,
                    cursor: 'pointer',
                    '&:hover': { opacity: 1, boxShadow: '0 2px 4px rgba(0,0,0,0.2)' },
                  }}
                  onClick={() => onBoxClick(tpId, activationsPeriod)}
                />
              </FHTooltip>
              {activationsPeriod.activationStart &&
                (!multipleActivations || (multipleActivations && isFirstActivation)) && (
                  <Typography
                    sx={{
                      position: 'absolute',
                      left: `${left}%`,
                      top: '50%',
                      width: 'fit-content',
                      transform: left < 10 ? 'translate(0%, -50%)' : 'translate(-100%, -50%)',
                      fontSize: 10,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                      fontWeight: 500,
                      color: FORTA_BLUE.primary,
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                      px: 1,
                      backgroundColor: FORTA_BLUE.light3,
                      borderRadius: '2px',
                    }}
                  >
                    {moment(activationsPeriod.activationStart).format(ACTIVATION_DATE_FORMAT)}
                  </Typography>
                )}
              {activationsPeriod.activationEnd &&
                (!multipleActivations || (multipleActivations && isLastActivation)) && (
                  <Typography
                    sx={{
                      position: 'absolute',
                      left: `${left + width}%`,
                      top: '50%',
                      width: 'fit-content',
                      transform:
                        left + width < 90 ? 'translate(0%, -50%)' : 'translate(-100%, -50%)',
                      fontSize: 10,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                      fontWeight: 500,
                      color: FORTA_BLUE.primary,
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                      px: 1,
                      backgroundColor: FORTA_BLUE.light3,
                      borderRadius: '2px',
                    }}
                  >
                    {moment(activationsPeriod.activationEnd).format(ACTIVATION_DATE_FORMAT)}
                  </Typography>
                )}
            </span>
          );
        })}
      </Box>
    </Box>
  );
}

export default TPRow;
